import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { ReputationBadge } from './ReputationBadge';

/**
 * "Campaigns that fit you" — the creator's half of discovery.
 *
 * The showcase is a filter a creator has to operate. This is the same set of
 * open campaigns ranked against what the account has *already* published: its
 * platforms, its categories, its rate-card floor. Nothing to type.
 *
 * The floor does real work here. A campaign whose budget sits under the
 * creator's own published minimum is dropped, not ranked low — it is not a
 * weak opportunity, it is a distraction from the ones that are real.
 */

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;

function CreatorOpportunities({ onOpen }) {
    const { t } = useTranslation();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get('/api/discover/opportunities/')
            .then(res => { if (alive) setResults(res.data.results || []); })
            .catch(() => { /* the interceptor surfaces auth/payment errors */ })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    if (loading) {
        return <div className="flex justify-center py-16"><div className="spinner" /></div>;
    }

    if (!results.length) {
        return (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-300 mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                </div>
                <p className="text-sm font-bold text-slate-600">{t('discover.forYouEmpty')}</p>
                <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">
                    {t('discover.forYouEmptyDesc')}
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed px-1">{t('discover.forYouHint')}</p>

            {results.map(campaign => (
                <button key={campaign.id} onClick={() => onOpen(campaign)}
                    className="w-full text-left rounded-2xl bg-white border border-slate-200 p-3.5 hover:shadow-md transition">
                    <div className="flex items-start gap-3">
                        <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                            {campaign.cover_image
                                ? <img src={campaign.cover_image} alt="" className="w-full h-full object-cover" />
                                : <div className="w-full h-full flex items-center justify-center text-slate-300 text-xl">◎</div>}
                        </div>

                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-bold text-slate-800 line-clamp-2 leading-snug">
                                {campaign.title}
                            </h3>
                            <div className="flex items-center gap-1.5 mt-1">
                                <span className="text-[11px] text-slate-500 truncate">
                                    {campaign.brand?.username}
                                </span>
                                <ReputationBadge rating={campaign.brand?.rating} compact />
                            </div>
                            {campaign.budget_usd > 0 && (
                                <p className="text-sm font-black text-[#0f8a7e] tabular-nums mt-1">
                                    {usd(campaign.budget_usd)}
                                </p>
                            )}
                        </div>

                        <div className="text-right flex-shrink-0">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide leading-none">
                                {t('discover.match')}
                            </p>
                            <p className="text-lg font-black text-primary-700 tabular-nums leading-tight">
                                {Math.round(campaign.match_score)}
                            </p>
                        </div>
                    </div>

                    {/* Why it is here — the same audit trail the brand side gets. */}
                    {campaign.reasons?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2.5">
                            {campaign.reasons.map((reason, index) => (
                                <span key={index}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-600 border border-slate-200 text-[10px] font-semibold">
                                    {t(`discover.reason_${reason.key}`)}
                                    {reason.value && <span className="font-bold text-slate-700">{reason.value}</span>}
                                </span>
                            ))}
                        </div>
                    )}
                </button>
            ))}
        </div>
    );
}

export default CreatorOpportunities;
